<template>
  <div class="home">
    <h2>Users</h2>
    <b-button @click="raisePopup({ target: $event.target })" variant="primary"
      >Add user</b-button
    >
    <!-- create user modal -->
    <b-modal
      id="createUserModal"
      @ok="createUserFormSubmit"
      :ok-disabled="!isFormValid"
      :title="isEdit ? 'Update password' : 'Create new user'"
    >
      <b-form>
        <b-form-group
          id="createUserEmailInputGroup1"
          label="Email:"
          label-for="createUserEmailInput1"
        >
          <b-form-input
            id="createUserEmailInput1"
            type="text"
            :disabled="isEdit"
            v-model="form.email"
            placeholder="Enter email"
          />
        </b-form-group>
        <b-form-group
          id="createUserPassInputGroup1"
          label="Password:"
          label-for="createUserPassInput1"
        >
          <b-form-input
            id="createUserPassInput1"
            type="text"
            v-model="form.password"
            placeholder="Enter password"
          />
        </b-form-group>
        <b-input-group
          id="createUserRoleInputGroup4"
          label="Role:"
          label-for="createUserRole"
        >
          <b-form-select
            :disabled="isEdit"
            v-model="form.role"
            id="createUserRole"
          >
            <option :value="null" disabled>Choose a role</option>
            <option value="admin">Admin</option>
            <option value="developer">Developer</option>
            <option value="tester">Tester</option>
            <option value="customercare">CustomerCare</option>
          </b-form-select>
        </b-input-group>
      </b-form>
    </b-modal>

    <b-container fluid>
      <b-row>
        <b-col md="6" class="my-1">
          <b-form-group label-cols-sm="2" label="Sort" class="mb-0">
            <b-input-group>
              <b-form-select v-model="sortBy" :options="sortOptions">
                <option slot="first" :value="null">-- none --</option>
              </b-form-select>
              <b-form-select
                :disabled="!sortBy"
                v-model="sortDesc"
                slot="append"
              >
                <option :value="false">Asc</option>
                <option :value="true">Desc</option>
              </b-form-select>
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col md="6" class="my-1">
          <b-form-group label-cols-sm="3" label="Per page" class="mb-0">
            <b-form-select :options="pageOptions" v-model="perPage" />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Main table element -->
      <b-table
        show-empty
        small
        hover
        stacked="md"
        :items="items"
        :fields="fields"
        :current-page="currentPage"
        :per-page="perPage"
        :filter="filter"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        @filtered="onFiltered"
        ref="usersTable"
        class="usersTable"
      >
        <template v-slot:cell(actions)="row">
          <b-button
            size="sm"
            class="m-1"
            variant="warning"
            @click="
              raisePopup({
                target: $event.target,
                isEdit: true,
                userData: row.item
              })
            "
            >✎</b-button
          >
          <b-button
            size="sm"
            variant="danger"
            class="m-1 ml-3"
            :disabled="row.item.role === 'admin'"
            @click="
              removeUser({
                target: $event.target,
                user: row.item
              })
            "
            >X</b-button
          >
        </template>
        <template v-slot:cell(active)="row">{{
          row.value ? 'Yes' : 'No'
        }}</template>

        <template v-slot:row-details="row">
          <b-card>
            <ul>
              <li v-for="(value, key) in row.item" :key="key">
                {{ key }}: {{ value }}
              </li>
            </ul>
          </b-card>
        </template>
      </b-table>

      <b-row>
        <b-col md="6" class="my-1">
          <b-pagination
            :total-rows="totalRows"
            :per-page="perPage"
            v-model="currentPage"
            class="my-0"
          />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
/* eslint-disable no-magic-numbers */
const originalForm = {
  email: null,
  password: null,
  role: null
};

export default {
  data() {
    return {
      currentPage: 1,
      perPage: 20,
      pageOptions: [20, 50, 100, 500],
      sortBy: null,
      sortDesc: false,
      filter: null,
      form: {
        ...originalForm
      },
      isEdit: false
    };
  },
  computed: {
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => {
          return { text: f.label, value: f.key };
        });
    },
    isFormValid() {
      if (!this.form.email) return false;
      if (!this.form.password) return false;
      if (!this.form.role) return false;
      return true;
    },
    fields() {
      return [
        {
          key: 'id',
          label: 'id',
          sortable: true,
          sortDirection: 'desc'
        },
        {
          key: 'email',
          label: 'E-mail',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'role',
          label: 'Role',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'createdAt',
          label: 'Created',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'updatedAt',
          label: 'Updated',
          sortable: true,
          class: 'text-center'
        },
        { key: 'actions', label: 'Actions' }
      ];
    },
    totalRows() {
      return this.$store.state.users.count;
    }
  },
  methods: {
    raisePopup({ target, isEdit = false, userData = null }) {
      this.isEdit = isEdit;
      if (userData && isEdit) {
        this.form = Object.keys(originalForm).reduce(
          (acc, current) =>
            userData[current]
              ? {
                  ...acc,
                  [current]: String(userData[current])
                }
              : acc,
          {}
        );
      } else {
        this.userDataFormReset();
      }
      this.$root.$emit('bv::show::modal', 'createUserModal', target);
    },
    userDataFormReset() {
      this.form = { ...originalForm };
    },
    async createUserFormSubmit(event) {
      if (this.isEdit) {
        await this.$store.dispatch('updateUser', {
          payload: this.form,
          event
        });
      } else {
        await this.$store.dispatch('addUser', {
          payload: this.form,
          event
        });
      }

      this.createUserFormReset();
      this.$refs.usersTable.refresh();
    },
    createUserFormReset() {
      this.form = { ...originalForm };
    },
    onFiltered() {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.currentPage = 1;
    },
    items(ctx) {
      const promise = this.$store.dispatch('getUsers', ctx);

      // Must return a promise that resolves to an array of items
      return promise.then(() => {
        return this.$store.state.users.rows || [];
      });
    },
    removeUser({ user = null }) {
      if (!user) {
        return;
      }
      const promise = this.$store.dispatch('removeUser', user.id);

      // Must return a promise that resolves to an array of items
      return promise.then(() => {
        this.$refs.usersTable.refresh();
      });
    }
  }
};
</script>
<style scoped type="text/scss">
.block {
  border-bottom: #dadada 1px solid;
  padding-bottom: 1em;
  margin-bottom: 3em;
}
.container {
  max-width: none;
}
.usersTable {
  font-size: 0.8rem;
}
.btn {
  font-size: 0.8rem;
}

td[data-label='Actions'] {
  white-space: nowrap;
}
</style>
